import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController, SearchbarCustomEvent } from '@ionic/angular';
import Env from '@ridango/core/env';
import { ApplicationFacade, Stop } from '@ridango/components';
import { debounceTime, distinctUntilChanged, takeUntil, map, startWith, scan } from 'rxjs/operators';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { BaseComponent } from '@ridango/core';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

export interface ExtendedStop extends Stop {
    selected?: boolean;
}

@Component({
    selector: 'rwidget-journey-planner-via-point-modal',
    templateUrl: './journey-planner-via-point-modal.component.html',
    styleUrls: ['./journey-planner-via-point-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class JourneyPlannerViaPointModalComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() stops: ExtendedStop[];
    @Input() selectedStopId: string;
    @Input() initialSearchTerm = '';

    filteredStops$: Observable<ExtendedStop[]>;
    displayedStops$: Observable<ExtendedStop[]>;
    hasMoreStops$: Observable<boolean>;
    selectedStop$: Observable<ExtendedStop | null>;
    isSearchbarFocused = false;
    searchInputValue = '';

    get searchTerm(): string {
        return this.searchTermSubject.value;
    }

    private readonly searchTermSubject = new BehaviorSubject<string>('');
    private readonly loadMoreSubject = new BehaviorSubject<boolean>(false);
    private readonly allStopsSubject = new BehaviorSubject<ExtendedStop[]>([]);

    readonly assetsPath$ = this.applicationFacade.assetsPath$;
    readonly pageSize = 20;
    readonly minSearchCharacters: number;
    readonly searchTerm$ = this.searchTermSubject.pipe(debounceTime(300), distinctUntilChanged());
    readonly allStops$ = this.allStopsSubject.asObservable();
    private keyboardWillShowListener?: PluginListenerHandle;
    private keyboardWillHideListener?: PluginListenerHandle;

    constructor(
        @Inject('ENV') public env: Env,
        private modalCtrl: ModalController,
        private applicationFacade: ApplicationFacade
    ) {
        super();
        this.minSearchCharacters = this.env.SEARCH_SETTINGS?.MINIMUM_SEARCH_CHARACTERS ?? 1;
    }

    ngOnInit(): void {
        this.setupKeyboardHandling();
        this.setSearchTerm(this.initialSearchTerm || '', false);

        const initialStops = this.stops.map((stop) => ({
            ...stop,
            selected: stop.id === this.selectedStopId
        }));
        this.allStopsSubject.next(initialStops);

        this.filteredStops$ = combineLatest([this.allStops$, this.searchTerm$]).pipe(
            map(([stops, searchTerm]) => {
                const normalizedTerm = this.normalizeSearch(searchTerm);

                if (normalizedTerm.length < this.minSearchCharacters) {
                    return [];
                }

                return stops.filter(
                    (stop) =>
                        stop.name.toLowerCase().includes(normalizedTerm) ||
                        (stop.code && stop.code.toLowerCase().includes(normalizedTerm))
                );
            }),
            takeUntil(this.componentDestroyed$)
        );

        this.displayedStops$ = combineLatest([
            this.filteredStops$,
            this.loadMoreSubject.pipe(
                scan((currentPage, loadMore) => (loadMore ? currentPage + 1 : 1), 0),
                startWith(1)
            )
        ]).pipe(
            map(([filteredStops, currentPage]) => {
                const endIndex = currentPage * this.pageSize;
                return filteredStops.slice(0, endIndex);
            }),
            takeUntil(this.componentDestroyed$)
        );

        this.hasMoreStops$ = combineLatest([this.filteredStops$, this.displayedStops$]).pipe(
            map(([filtered, displayed]) => displayed.length < filtered.length)
        );

        this.selectedStop$ = this.allStops$.pipe(map((stops) => stops.find((stop) => stop.selected) || null));
    }

    ngOnDestroy(): void {
        // Reset keyboard resize mode and remove listeners to avoid side effects and leaks.
        this.keyboardWillShowListener?.remove();
        this.keyboardWillHideListener?.remove();
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
            Keyboard.setResizeMode({ mode: KeyboardResize.Ionic }).catch(() => undefined);
        }
    }

    /**
     * Disable iOS keyboard resizing and manually track show/hide events for stable layout.
     */
    private async setupKeyboardHandling(): Promise<void> {
        if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;

        await Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(() => undefined);

        this.keyboardWillShowListener = await Keyboard.addListener('keyboardWillShow', () => {
            this.isSearchbarFocused = true;
        });

        this.keyboardWillHideListener = await Keyboard.addListener('keyboardWillHide', () => {
            this.isSearchbarFocused = false;
        });
    }

    loadMoreStops(): void {
        this.loadMoreSubject.next(true);
    }

    toggleStop(stop: ExtendedStop): void {
        const currentStops = this.allStopsSubject.value;
        const updatedStops = currentStops.map((s) => ({
            ...s,
            selected: s.id === stop.id
        }));

        this.allStopsSubject.next(updatedStops);
        this.chooseStop(stop);
    }

    onSearchFocus(): void {
        this.isSearchbarFocused = true;
    }

    onSearchBlur(): void {
        this.isSearchbarFocused = false;
    }

    public filterStops(event: SearchbarCustomEvent): void {
        // We read the value from two different places because Ionic sometimes reports input differently
        // depending on the platform (iOS/Android/Web) and event type. `detail.value` is the recommended
        // way in Ionic events, but it is not always populated. `target.value` is the fallback for
        // standard DOM events. We combine both to ensure we always capture the actual search input.
        const detailValue = event?.detail?.value;
        const targetValue = (event?.target as HTMLIonSearchbarElement)?.value;
        const rawValue = (detailValue ?? targetValue ?? '').toString();

        this.setSearchTerm(rawValue);
    }

    public closeModal(): void {
        this.modalCtrl.dismiss({ searchTerm: this.searchInputValue }, 'cancel');
    }

    private chooseStop(selectedStop: ExtendedStop): void {
        this.modalCtrl.dismiss(
            {
                stop: selectedStop,
                searchTerm: this.searchInputValue
            },
            'selected'
        );
    }

    private setSearchTerm(value: string, resetPagination: boolean = true): void {
        const normalizedValue = this.normalizeDisplayValue(value);
        this.searchInputValue = normalizedValue;
        this.searchTermSubject.next(normalizedValue);

        if (resetPagination) {
            this.loadMoreSubject.next(false);
        }
    }

    private normalizeDisplayValue(value: string): string {
        return (value ?? '').toString().trim();
    }

    private normalizeSearch(value: string): string {
        return this.normalizeDisplayValue(value).toLowerCase();
    }
}
